import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'react-bootstrap';
import { salesAPI, clientsAPI } from '../utils/api'; // Adjust the import path
import '../css/SalesManagement.css';

const SalesManagement = () => {
    const navigate = useNavigate();
    const [sales, setSales] = useState([]); // This state holds the client accounts data
    const [filteredSales, setFilteredSales] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [executiveOfficers, setExecutiveOfficers] = useState([]);
    const [loading, setLoading] = useState({
        sales: true,
        officers: true,
        recent: true // Added loading state for recent transactions
    });
    const [error, setError] = useState(null);
    const [selectedOfficer, setSelectedOfficer] = useState(null);
    const [recentTransactions, setRecentTransactions] = useState([]); // New state for recent transactions

    // Pagination state for Client Accounts
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    // Pagination state for Recent Transactions
    const [recentCurrentPage, setRecentCurrentPage] = useState(1);
    const recentRowsPerPage = 10;

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [recentSortConfig, setRecentSortConfig] = useState({ key: null, direction: 'asc' });

    // Fetch executive officers from API
    const fetchExecutiveOfficers = async () => {
        try {
            const response = await salesAPI.getExecutiveOfficers();
            setExecutiveOfficers(response.data);
        } catch (err) {
            // console.error('Error fetching executive officers:', err);
            // Fallback to static data if API fails
            setExecutiveOfficers([
                { id: 1, name: 'John Smith', role: 'Sales Director' },
                { id: 2, name: 'Sarah Johnson', role: 'Account Manager' },
                { id: 3, name: 'Michael Chen', role: 'Senior Sales Executive' },
                { id: 4, name: 'Emma Wilson', role: 'Client Relations Manager' },
                { id: 5, name: 'Robert Davis', role: 'Sales Executive' }
            ]);
        } finally {
            setLoading(prev => ({ ...prev, officers: false }));
        }
    };

    // Fetch all sales data (Original Client Accounts)
    const fetchSalesData = async () => {
        try {
            setLoading(prev => ({ ...prev, sales: true }));
            const response = await salesAPI.getAll();
            let data = response.data;

            // Sort by name alphabetically (or keep backend sort)
            data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            // Transform API data to match frontend structure for client accounts
            const transformedData = data.map(item => ({
                id: item.client_id,
                customerId: item.client_id,
                clientName: item.name,
                date: item.latest_date ? new Date(item.latest_date).toLocaleDateString('en-IN') : 'No Transactions',
                clientType: item.client_type,
                openingBalance: parseFloat(item.opening_balance) || 0,
                executiveOfficer: item.executive_officer_name || 'Not Assigned',
                currentBalance: parseFloat(item.current_balance) || 0
            }));

            setSales(transformedData);
            setFilteredSales(transformedData);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load client accounts');
            setSales([]);
            setFilteredSales([]);
        } finally {
            setLoading(prev => ({ ...prev, sales: false }));
        }
    };

    // Fetch Recent Transactions (Last 100)
    const fetchRecentTransactions = async () => {
        try {
            setLoading(prev => ({ ...prev, recent: true }));

            // Fetch last 100 transactions without date constraint
            const response = await clientsAPI.getOverallSales({ limit: 100 });
            const data = response.data || [];

            const transformedData = data.map(item => ({
                id: item.sale_transaction_id,
                customerId: item.client_id,
                clientName: item.client_name,
                date: new Date(item.transaction_date).toLocaleDateString('en-IN'),
                billNo: item.bill_no || 'N/A',
                branchName: item.branch_name || 'N/A',
                type: item.transaction_type,
                grandTotal: parseFloat(item.grand_total) || 0,
                amountPaid: parseFloat(item.amount_paid) || 0,
                balance: parseFloat(item.balance) || 0
            }));

            setRecentTransactions(transformedData);
        } catch (err) {
            console.error('Error fetching recent transactions:', err);
            setRecentTransactions([]);
        } finally {
            setLoading(prev => ({ ...prev, recent: false }));
        }
    };

    useEffect(() => {
        // Fetch initial data
        fetchExecutiveOfficers();
        fetchSalesData();
        fetchRecentTransactions();
    }, []);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredSales(sales);
            return;
        }
        const q = searchQuery.toLowerCase();
        setFilteredSales(sales.filter(s =>
            s.clientName.toLowerCase().includes(q)
        ));
    }, [searchQuery, sales]);

    // Reset to page 1 whenever filtered results change
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredSales]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const handleViewDetails = (clientId) => {
        navigate(`/customer-sales/${clientId}`);
    };

    const handleOfficerSelect = (officerName) => {
        // Navigate to the fixed URL as requested by the user
        navigate(`/sales/clients-by-officer/${encodeURIComponent(officerName)}`);
    };

    const handleClearFilters = async () => {
        setSearchQuery('');
        setSelectedOfficer(null);
        await fetchSalesData(); // Refetch all sales data
    };

    // Sorting Logic
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleRecentSort = (key) => {
        let direction = 'asc';
        if (recentSortConfig.key === key && recentSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setRecentSortConfig({ key, direction });
    };

    const sortData = (data, config) => {
        if (!config.key) return data;

        return [...data].sort((a, b) => {
            let valA = a[config.key];
            let valB = b[config.key];

            if (config.key === 'date') {
                if (valA === 'No Transactions') valA = new Date(0);
                else if (typeof valA === 'string' && valA.includes('/')) {
                    const [d, m, y] = valA.split('/');
                    valA = new Date(`${y}-${m}-${d}`);
                } else {
                    valA = new Date(valA || 0);
                }

                if (valB === 'No Transactions') valB = new Date(0);
                else if (typeof valB === 'string' && valB.includes('/')) {
                    const [d, m, y] = valB.split('/');
                    valB = new Date(`${y}-${m}-${d}`);
                } else {
                    valB = new Date(valB || 0);
                }
            } else if (typeof valA === 'string') {
                valA = valA.toLowerCase();
                valB = (valB || '').toLowerCase();
            }

            if (valA < valB) return config.direction === 'asc' ? -1 : 1;
            if (valA > valB) return config.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const sortedSales = sortData(filteredSales, sortConfig);
    const sortedRecentTransactions = sortData(recentTransactions, recentSortConfig);

    const renderSortArrow = (config, key) => {
        if (config.key !== key) return <i className="bi bi-arrow-down-up text-muted ms-1 small opacity-50"></i>;
        return config.direction === 'asc'
            ? <i className="bi bi-arrow-up text-primary ms-1 small fw-bold"></i>
            : <i className="bi bi-arrow-down text-primary ms-1 small fw-bold"></i>;
    };

    // Pagination helpers
    const totalRows = sortedSales.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedSales = sortedSales.slice(startIndex, endIndex);

    const goToPage = (page) => {
        const p = Math.min(Math.max(1, page), totalPages);
        setCurrentPage(p);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        let items = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => goToPage(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4 mb-5">
                <Pagination className="pagination-premium mb-0">
                    <Pagination.First onClick={() => goToPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} />
                    {startPage > 1 && <Pagination.Ellipsis disabled />}
                    {items}
                    {endPage < totalPages && <Pagination.Ellipsis disabled />}
                    <Pagination.Next onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
                <div className="ms-3 text-muted small fw-bold bg-white px-3 py-2 rounded shadow-sm border">
                    Page {currentPage} of {totalPages} — {totalRows} records
                </div>
            </div>
        );
    };

    // Pagination helpers for recent transactions
    const recentTotalRows = sortedRecentTransactions.length;
    const recentTotalPages = Math.max(1, Math.ceil(recentTotalRows / recentRowsPerPage));
    const recentStartIndex = (recentCurrentPage - 1) * recentRowsPerPage;
    const recentEndIndex = recentStartIndex + recentRowsPerPage;
    const paginatedRecentSales = sortedRecentTransactions.slice(recentStartIndex, recentEndIndex);

    const goToRecentPage = (page) => {
        const p = Math.min(Math.max(1, page), recentTotalPages);
        setRecentCurrentPage(p);
    };

    const renderRecentPagination = () => {
        if (recentTotalPages <= 1) return null;

        let items = [];
        let startPage = Math.max(1, recentCurrentPage - 2);
        let endPage = Math.min(recentTotalPages, startPage + 4);

        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === recentCurrentPage}
                    onClick={() => goToRecentPage(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
                <Pagination className="pagination-premium mb-0">
                    <Pagination.First onClick={() => goToRecentPage(1)} disabled={recentCurrentPage === 1} />
                    <Pagination.Prev onClick={() => goToRecentPage(recentCurrentPage - 1)} disabled={recentCurrentPage === 1} />
                    {startPage > 1 && <Pagination.Ellipsis disabled />}
                    {items}
                    {endPage < recentTotalPages && <Pagination.Ellipsis disabled />}
                    <Pagination.Next onClick={() => goToRecentPage(recentCurrentPage + 1)} disabled={recentCurrentPage === recentTotalPages} />
                    <Pagination.Last onClick={() => goToRecentPage(recentTotalPages)} disabled={recentCurrentPage === recentTotalPages} />
                </Pagination>
                <div className="ms-3 text-muted small fw-bold bg-white px-3 py-2 rounded shadow-sm border">
                    Page {recentCurrentPage} of {recentTotalPages} — {recentTotalRows} records
                </div>
            </div>
        );
    };

    return (
        <div className="sales-accounts-container">
            <h1>Sales Management</h1>

            {/* Executive Officers Buttons at the top */}
            <div className="executive-officers-section mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-bold">Executive Officers</h5>
                    {selectedOfficer && (
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClearFilters}
                            disabled={loading.sales}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                {loading.officers ? (
                    <div className="text-muted small">Loading officers...</div>
                ) : (
                    <div className="officers-buttons d-flex flex-wrap gap-2">
                        {executiveOfficers.map((officer) => (
                            <button
                                key={officer.id}
                                className={`btn btn-sm ${selectedOfficer === officer.name ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleOfficerSelect(officer.name)}
                                title={`${officer.role}: ${officer.name}`}
                                disabled={loading.sales}
                            >
                                {officer.name}
                                {selectedOfficer === officer.name && (
                                    <span className="ms-2">✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
                {selectedOfficer && (
                    <div className="mt-2 text-muted small">
                        Showing accounts for: <strong>{selectedOfficer}</strong>
                    </div>
                )}
            </div>

            {/* Client Accounts Table */}
            <div className="client-accounts-section">
                <div className="d-flex justify-content-between align-items-md-center flex-column flex-md-row mb-4 gap-3">
                    <h4 className="mb-0 fw-bold text-dark">Client Accounts</h4>

                    {/* Search Bar Inline */}
                    <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </span>
                            <input
                                type="text"
                                placeholder="Search by client name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="form-control border-start-0"
                                disabled={loading.sales}
                            />
                            {searchQuery && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setSearchQuery('')}
                                    type="button"
                                    disabled={loading.sales}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-warning mb-4" role="alert">
                        {error}
                    </div>
                )}

                <div className="card shadow-sm border-0 mb-5">
                    <div className="card-body p-0">
                        {loading.sales ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading client accounts...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="sales-table table table-hover table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th onClick={() => handleSort('clientName')} style={{ cursor: 'pointer' }}>Client Name {renderSortArrow(sortConfig, 'clientName')}</th>
                                                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>Created Date {renderSortArrow(sortConfig, 'date')}</th>
                                                <th onClick={() => handleSort('clientType')} style={{ cursor: 'pointer' }}>Client Type {renderSortArrow(sortConfig, 'clientType')}</th>
                                                <th onClick={() => handleSort('openingBalance')} style={{ cursor: 'pointer' }}>Opening Balance {renderSortArrow(sortConfig, 'openingBalance')}</th>
                                                <th onClick={() => handleSort('executiveOfficer')} style={{ cursor: 'pointer' }}>Executive Officer {renderSortArrow(sortConfig, 'executiveOfficer')}</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedSales.length > 0 ? (
                                                paginatedSales.map((row) => (
                                                    <tr key={row.id} className="sale-row">
                                                        <td>{row.clientName}</td>
                                                        <td>{row.date}</td>
                                                        <td>{row.clientType}</td>
                                                        <td>{formatCurrency(row.openingBalance)}</td>
                                                        <td>{row.executiveOfficer}</td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleViewDetails(row.customerId)}
                                                                disabled={loading.sales}
                                                            >
                                                                View Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5 text-muted">
                                                        {searchQuery
                                                            ? `No clients found matching "${searchQuery}"`
                                                            : 'No client accounts available'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Pagination controls */}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <hr className="my-5" />

            {/* Recent Transactions Section */}
            <div className="recent-transactions-section mb-5">
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-primary text-white py-3">
                        <h5 className="mb-0 fw-bold">Recent Transactions (Last 100)</h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover table-striped mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th onClick={() => handleRecentSort('date')} style={{ cursor: 'pointer' }}>Date {renderSortArrow(recentSortConfig, 'date')}</th>
                                        <th onClick={() => handleRecentSort('clientName')} style={{ cursor: 'pointer' }}>Client Name {renderSortArrow(recentSortConfig, 'clientName')}</th>
                                        <th onClick={() => handleRecentSort('billNo')} style={{ cursor: 'pointer' }}>Bill No {renderSortArrow(recentSortConfig, 'billNo')}</th>
                                        <th onClick={() => handleRecentSort('branchName')} style={{ cursor: 'pointer' }}>Branch {renderSortArrow(recentSortConfig, 'branchName')}</th>
                                        <th onClick={() => handleRecentSort('type')} style={{ cursor: 'pointer' }}>Type {renderSortArrow(recentSortConfig, 'type')}</th>
                                        <th onClick={() => handleRecentSort('grandTotal')} style={{ cursor: 'pointer' }}>Total {renderSortArrow(recentSortConfig, 'grandTotal')}</th>
                                        <th onClick={() => handleRecentSort('amountPaid')} style={{ cursor: 'pointer' }}>Paid {renderSortArrow(recentSortConfig, 'amountPaid')}</th>
                                        <th onClick={() => handleRecentSort('balance')} style={{ cursor: 'pointer' }}>Balance {renderSortArrow(recentSortConfig, 'balance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading.recent ? (
                                        <tr><td colSpan="8" className="text-center py-4">Loading recent transactions...</td></tr>
                                    ) : paginatedRecentSales.length > 0 ? (
                                        paginatedRecentSales.map((tx) => (
                                            <tr key={tx.id}>
                                                <td>{tx.date}</td>
                                                <td>{tx.clientName}</td>
                                                <td>{tx.billNo}</td>
                                                <td>{tx.branchName}</td>
                                                <td>
                                                    <span className={`badge ${tx.type === 'Return' ? 'bg-danger' : 'bg-success'}`}>
                                                        {tx.type}
                                                    </span>
                                                </td>
                                                <td>{formatCurrency(tx.grandTotal)}</td>
                                                <td>{formatCurrency(tx.amountPaid)}</td>
                                                <td className={tx.balance > 0 ? 'text-danger fw-bold' : 'text-success'}>
                                                    {formatCurrency(tx.balance)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="8" className="text-center py-4 text-muted">No transactions found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Recent Transactions Pagination controls */}
                        {renderRecentPagination()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesManagement;